import {addMonths, endOfMonth, format, getDay, getDaysInMonth, isBefore, isSameDay, startOfDay, startOfMonth} from 'date-fns';
import ru from 'date-fns/locale/ru';
import enUS from 'date-fns/locale/en-US';
import {host} from '../const/const';
import formatDate from './format-date';

export const WEEKDAYS = {
    ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
};

export function formatMonthTitle(date, lang) {
    if (!date) {
        return '';
    }

    const locale = lang === 'en' ? enUS : ru;
    const result = format(date, 'LLLL yyyy', { locale });

    return result.charAt(0).toUpperCase() + result.slice(1);
}

export function getMonthDays(monthDate) {
    const start = startOfMonth(monthDate);
    const daysInMonth = getDaysInMonth(start);
    const weekday = getDay(start);
    const offset = weekday === 0 ? 6 : weekday - 1;
    const days = Array.from({length: offset}, () => null);

    for (let day = 1; day <= daysInMonth; day++) {
        days.push(new Date(start.getFullYear(), start.getMonth(), day));
    }

    return days;
}

export function isPastDay(date, currentDate) {
    if (!date || !currentDate) {
        return false;
    }
    return isBefore(startOfDay(date), startOfDay(currentDate));
}

export function addDaysToDate(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + Number(days || 0));
    return result;
}

export function getCalendarDaysDiff(start, finish) {
    if (!start || !finish) {
        return null;
    }
    const a = startOfDay(start).getTime();
    const b = startOfDay(finish).getTime();
    return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

export function normalizeMinRentDays(minRentDays) {
    const parsed = parseInt(minRentDays, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function getMinFinishDate(start, minRentDays) {
    if (!start) {
        return null;
    }
    return addDaysToDate(start, normalizeMinRentDays(minRentDays));
}

export function ensureMinRentFinish(start, finish, minRentDays) {
    if (!start) {
        return finish || null;
    }
    const minFinish = getMinFinishDate(start, minRentDays);
    if (!finish) {
        return minFinish;
    }
    const diff = getCalendarDaysDiff(start, finish);
    if (diff == null || diff < normalizeMinRentDays(minRentDays)) {
        return minFinish;
    }
    return finish;
}

export function getMinStartHourForDate(date, currentDate) {
    if (!date || !currentDate) {
        return 0;
    }
    if (!isSameDaySafe(date, currentDate)) {
        return 0;
    }
    return currentDate.getHours();
}

export function isTooEarlyFinishDay(date, start, minRentDays) {
    if (!date || !start) {
        return false;
    }
    const diff = getCalendarDaysDiff(start, date);
    if (diff == null || diff <= 0) {
        return false;
    }
    return diff < normalizeMinRentDays(minRentDays);
}

export function isSameDaySafe(a, b) {
    if (!a || !b) {
        return false;
    }
    return isSameDay(a, b);
}

export function isInRange(date, start, end) {
    if (!date || !start || !end) {
        return false;
    }
    const time = startOfDay(date).getTime();
    return time > startOfDay(start).getTime() && time < startOfDay(end).getTime();
}

export function shiftMonth(date, amount) {
    return addMonths(date, amount);
}

export function formatDayKey(date) {
    if (!date) {
        return '';
    }
    return format(date, 'yyyy-MM-dd');
}

export function getVisibleMonthsRange(viewMonth) {
    const start = startOfMonth(viewMonth);
    const end = endOfMonth(addMonths(viewMonth, 1));
    return {start, end};
}

export function normalizePeriodSales(raw) {
    if (!Array.isArray(raw) || !raw.length) {
        return null;
    }

    const items = raw
        .map((item) => {
            const days = item?.days != null ? String(item.days).trim() : '';
            const percent = parseInt(item?.percent, 10);
            if (!days || !Number.isFinite(percent) || percent <= 0) {
                return null;
            }
            return {days, percent};
        })
        .filter(Boolean);

    return items.length ? items : null;
}

export function formatPeriodSaleDays(days, lang = 'ru') {
    const range = days != null ? String(days).trim() : '';
    if (!range) {
        return '';
    }

    const openEnded = range.match(/^(\d+)\s*-\s*999$/);
    if (openEnded) {
        const from = openEnded[1];
        return lang === 'en' ? `from ${from}+ days` : `от ${from}+ дней`;
    }

    return lang === 'en' ? `from ${range} days` : `от ${range} дней`;
}

export async function fetchCarPricesResponse({id, carid, dtstart, dtend, signal}) {
    const params = new URLSearchParams({
        action: 'get_car_prices',
        dtstart,
        dtend,
    });
    if (id) {
        params.set('id', id);
    }
    if (carid) {
        params.set('carid', carid);
    }

    const res = await fetch(`${host}?${params.toString()}`, {signal});
    const response = await res.json();

    if (!response?.success || !response?.prices) {
        return null;
    }

    return {
        prices: response.prices,
        periodSales: normalizePeriodSales(response.periodSales),
    };
}

export async function fetchCarPrices({id, carid, dtstart, dtend, signal}) {
    const data = await fetchCarPricesResponse({id, carid, dtstart, dtend, signal});
    return data?.prices?.days || {};
}

export function isSupersaleDay(item) {
    if (!item) {
        return false;
    }
    const value = item.supersale;
    return value === true || value === 1 || value === '1';
}

export function isSaleDay(item) {
    if (!item) {
        return false;
    }
    const value = item.sale;
    return value === true || value === 1 || value === '1';
}

/** Акционный день: SuperSale или sale — лояльность/промокод не применяются */
export function isPromoPriceDay(item) {
    return isSupersaleDay(item) || isSaleDay(item);
}

export function getDayActionPrice(item) {
    const regular = parseInt(item?.price, 10) || 0;
    if (regular <= 0) {
        return {base: 0, kind: null, discount: 0};
    }

    if (isSupersaleDay(item)) {
        const supersalePrice = parseInt(item?.supersale_price, 10) || 0;
        if (supersalePrice > 0) {
            return {
                base: supersalePrice,
                kind: 'supersale',
                discount: regular > supersalePrice ? regular - supersalePrice : 0,
            };
        }
        return {base: regular, kind: 'supersale', discount: 0};
    }

    if (isSaleDay(item)) {
        const salePrice = parseInt(item?.sale_price, 10) || 0;
        if (salePrice > 0) {
            return {
                base: salePrice,
                kind: 'sale',
                discount: regular > salePrice ? regular - salePrice : 0,
            };
        }
        return {base: regular, kind: 'sale', discount: 0};
    }

    return {base: regular, kind: null, discount: 0};
}

export function getLoyaltySalePercent(user) {
    const value = user?.sale;
    if (value == null || String(value).trim() === '') {
        return 0;
    }
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function getPersonalSalePercent(user, promoCodeSale = 0, {exclusivePromo = false} = {}) {
    const loyalty = getLoyaltySalePercent(user);
    const promo = parseInt(promoCodeSale, 10);
    const promoSafe = Number.isFinite(promo) && promo > 0 ? promo : 0;

    if (exclusivePromo && promoSafe > 0) {
        return {percent: promoSafe, type: 'promo'};
    }
    if (loyalty > 0 && promoSafe > 0) {
        return loyalty >= promoSafe
            ? {percent: loyalty, type: 'card'}
            : {percent: promoSafe, type: 'promo'};
    }
    if (loyalty > 0) {
        return {percent: loyalty, type: 'card'};
    }
    if (promoSafe > 0) {
        return {percent: promoSafe, type: 'promo'};
    }
    return {percent: 0, type: null};
}

export function applyPercentDiscount(price, salePercent) {
    const amount = parseInt(price, 10);
    const sale = parseInt(salePercent, 10);
    if (!amount || amount <= 0 || !sale || sale <= 0) {
        return amount > 0 ? amount : null;
    }
    return Math.round(amount * (100 - sale) / 100);
}

/** @deprecated use applyPercentDiscount */
export function applyLoyaltyDiscount(price, salePercent) {
    return applyPercentDiscount(price, salePercent);
}

export function parsePeriodSaleRange(days) {
    const raw = days != null ? String(days).trim() : '';
    const match = raw.match(/^(\d+)\s*-\s*(\d+)$/);
    if (!match) {
        return null;
    }
    const min = parseInt(match[1], 10);
    const max = parseInt(match[2], 10);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < min) {
        return null;
    }
    return {min, max};
}

export function getPeriodSalePercent(periodSales, daysCount) {
    const count = parseInt(daysCount, 10);
    if (!Array.isArray(periodSales) || !periodSales.length || !(count > 0)) {
        return 0;
    }

    let best = 0;
    for (let i = 0; i < periodSales.length; i++) {
        const item = periodSales[i];
        const range = parsePeriodSaleRange(item?.days);
        const percent = parseInt(item?.percent, 10);
        if (!range || !Number.isFinite(percent) || percent <= 0) {
            continue;
        }
        if (count >= range.min && count <= range.max && percent > best) {
            best = percent;
        }
    }
    return best;
}

/**
 * V2 day price stack:
 * 1) regular / supersale / sale base
 * 2) periodSales % on that base (all days)
 * 3) loyalty/promo % only on non-promo days
 */
export function calcV2RentFromDays(days, {
    salePercent = 0,
    periodSalePercent = 0,
    daysCount = null,
} = {}) {
    const map = days || {};
    let tariffTotal = 0;
    let rentGross = 0;
    let loyaltyDiscount = 0;
    let superSaleDiscount = 0;
    let daySaleDiscount = 0;
    let periodSaleDiscount = 0;
    let pricedDays = 0;

    Object.keys(map).sort().forEach((key) => {
        const item = map[key];
        const regular = parseInt(item?.price, 10) || 0;
        if (regular <= 0) {
            return;
        }

        pricedDays += 1;
        rentGross += regular;

        const {base, kind, discount} = getDayActionPrice(item);
        if (kind === 'supersale') {
            superSaleDiscount += discount;
        } else if (kind === 'sale') {
            daySaleDiscount += discount;
        }

        const afterPeriod = applyPercentDiscount(base, periodSalePercent) || base;
        if (afterPeriod < base) {
            periodSaleDiscount += base - afterPeriod;
        }

        let paid = afterPeriod;
        if (!kind && salePercent > 0) {
            paid = applyPercentDiscount(afterPeriod, salePercent) || afterPeriod;
            if (paid < afterPeriod) {
                loyaltyDiscount += afterPeriod - paid;
            }
        }

        tariffTotal += paid;
    });

    const divisor = daysCount > 0 ? daysCount : pricedDays;
    const avgPerDay = divisor > 0 ? Math.round(tariffTotal / divisor) : 0;

    return {
        tariffTotal: Math.round(tariffTotal),
        rentGross: Math.round(rentGross),
        avgPerDay,
        loyaltyDiscount: Math.round(loyaltyDiscount),
        superSaleDiscount: Math.round(superSaleDiscount),
        daySaleDiscount: Math.round(daySaleDiscount),
        periodSaleDiscount: Math.round(periodSaleDiscount),
        periodSalePercent: parseInt(periodSalePercent, 10) || 0,
        pricedDays,
    };
}

export function getDayPriceInfo(date, days, salePercent = 0, periodSalePercent = 0) {
    if (!date || !days) {
        return {price: null, isPromo: false, isSale: false};
    }

    const item = days[formatDayKey(date)];
    if (!item) {
        return {price: null, isPromo: false, isSale: false};
    }

    const basePrice = parseInt(item.price, 10);
    if (!(basePrice > 0)) {
        return {price: null, isPromo: false, isSale: false};
    }

    const {base, kind} = getDayActionPrice(item);
    const afterPeriod = applyPercentDiscount(base, periodSalePercent) || base;
    const isPromo = kind === 'supersale';
    const isSale = kind === 'sale';

    if (kind) {
        return {
            price: afterPeriod,
            isPromo,
            isSale,
        };
    }

    return {
        price: applyPercentDiscount(afterPeriod, salePercent) || afterPeriod,
        isPromo: false,
        isSale: false,
    };
}

export function getSupersaleKeys(days) {
    if (!days) {
        return [];
    }

    return Object.keys(days)
        .filter((key) => isSupersaleDay(days[key]))
        .sort();
}

export function getFirstSupersaleDayKey(days, currentDate) {
    const keys = getSupersaleKeys(days);

    if (!keys.length) {
        return null;
    }

    if (!currentDate) {
        return keys[0];
    }

    for (let i = 0; i < keys.length; i++) {
        const [year, month, day] = keys[i].split('-').map(Number);
        const date = new Date(year, month - 1, day);
        if (!isPastDay(date, currentDate)) {
            return keys[i];
        }
    }

    return null;
}

export function getSupersaleBounds(days, currentDate) {
    const keys = getSupersaleKeys(days);

    if (!keys.length) {
        return {startKey: null, endKey: null};
    }

    return {
        startKey: getFirstSupersaleDayKey(days, currentDate),
        endKey: keys[keys.length - 1],
    };
}

export function formatRangeDates(viewMonth) {
    const {start, end} = getVisibleMonthsRange(viewMonth);
    return {
        dtstart: formatDate(start),
        dtend: formatDate(end),
    };
}

export function formatCellPrice(price) {
    if (!price) {
        return '';
    }
    return `${parseInt(price, 10).toLocaleString('ru-RU')} ₽`;
}
